import { IconExternal } from "@/components/icons";

/**
 * First-party JTech promotional panel (not third-party advertising).
 *
 * Purple gradient panel shown as the tall right column on desktop and as a
 * card below the trust grid on mobile. Links navigate to jtechuk.com; no
 * analytics or tracking is attached (JDoc stays zero-telemetry).
 */
export function JTechPromoPanel({ compact = false }: { compact?: boolean }) {
  return (
    <aside
      className={[
        "jtech-gradient relative flex h-full flex-col overflow-hidden rounded-2xl text-white",
        compact ? "p-6" : "p-8",
      ].join(" ")}
    >
      {/* Decorative privacy motif — a document, a threading line, and a
          padlock — non-interactive. Deliberately spans the panel's full
          height so the lower portion (below the links) reads as designed
          rather than empty. Kept narrow and hugging the right edge so it
          never sits under the message text. */}
      {!compact && (
        <>
          {/* Soft warm glow behind the padlock for a little depth. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-8 right-6 h-28 w-28 rounded-full bg-jtech-mango/10 blur-2xl"
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 120 480"
            preserveAspectRatio="xMidYMid meet"
            className="pointer-events-none absolute right-3 top-0 h-full w-20 text-white/[0.11]"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M40 20h28l16 16v40a6 6 0 0 1-6 6H40a6 6 0 0 1-6-6V26a6 6 0 0 1 6-6z" />
            <path d="M68 20v16h16" />
            <path d="M46 48h20M46 60h24M46 72h14" />
            <path d="M60 94c14 26-10 54 4 82s-14 50 0 78-10 50 4 78" strokeDasharray="4 6" />
            <rect x="30" y="392" width="40" height="34" rx="7" />
            <path
              d="M38 392v-14a12 12 0 0 1 24 0v14"
              className="text-jtech-mango/40"
            />
            <circle cx="50" cy="407" r="3" fill="currentColor" stroke="none" />
          </svg>
        </>
      )}
      {compact && (
        <svg
          aria-hidden="true"
          viewBox="0 0 120 120"
          className="pointer-events-none absolute right-5 top-6 h-24 w-24 text-white/15"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M40 12h28l16 16v40a6 6 0 0 1-6 6H40a6 6 0 0 1-6-6V18a6 6 0 0 1 6-6z" />
          <path d="M68 12v16h16" />
          <path d="M46 40h20M46 52h24M46 64h14" />
          <path d="M84 84c14 6 6 22 20 26" strokeDasharray="4 5" />
        </svg>
      )}

      {/* All real content sits in one positioned stacking layer, above the
          decorative artwork behind it. */}
      <div className="relative z-10 flex h-full flex-col">
        <div className="flex items-center gap-2.5">
          <img
            src="/images/logo-jtech.png"
            alt="JTech"
            className="h-8 w-auto object-contain"
          />
          <span className="text-xl font-bold tracking-tight">JTech</span>
        </div>

        {!compact && (
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-jtech-mango">
            Built by JTech
          </p>
        )}

        <p
          className={[
            "text-white",
            compact
              ? "mt-4 max-w-xs text-base leading-relaxed"
              : "mt-3 max-w-[17rem] text-xl leading-relaxed",
          ].join(" ")}
        >
          We build simple, private tools that put you in control of your data.
        </p>

        <a
          href="https://jtechuk.com"
          target="_blank"
          rel="noopener noreferrer"
          className={[
            "font-extrabold tracking-tight text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70",
            compact ? "mt-5 text-3xl" : "mt-4 text-3xl",
          ].join(" ")}
        >
          jtechuk.com
        </a>

        <a
          href="https://jtechuk.com"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex w-fit items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-bold text-jtech-mango shadow-sm transition-colors hover:bg-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          Learn more
          <IconExternal className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">(opens jtechuk.com in a new tab)</span>
        </a>
      </div>
    </aside>
  );
}
